import { Button, Icon } from '@sgme/ui';
import { clsx } from 'clsx';
import { type CSSProperties, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useQueuedDispatch } from '@/App/tiles/Option/QueueContext.tsx';
import { StrategyFieldHeaders } from '@/App/tiles/Option/strategy/StrategyFieldHeaders.tsx';
import { getStrategyFields, hasTriggerOrBinary } from '@/App/tiles/Option/strategy/strategyFields.ts';
import { getStrategySections } from '@/App/tiles/Option/strategy/strategyGrouping.ts';
import { getFieldSpan, isHedgeField } from '@/App/tiles/Option/strategy/strategyLayout.ts';
import { StrategyPanelHeader } from '@/App/tiles/Option/strategy/StrategyPanelHeader.tsx';
import { StrategySection } from '@/App/tiles/Option/strategy/StrategySection';
import type { StrategyOrientation } from '@/App/tiles/Option/strategy/types.ts';
import { useUserContext } from '@/context/UserContext.tsx';

import './StrategyPanel.css';
import { getQuote, selectGroupedByExpiry } from '@/store/option/selectors/optionSelectors.ts';
import { addOptionLegThunk } from '@/store/option/thunks/thunk.ts';

type StrategyPanelProps = {
  tileId: string;
};

/**
 * Expiry grouping cases:
 * - Disabled: keep the original per-leg grid, without group headers or summaries.
 * - Enabled: group by expiry; a single group always stays open.
 * - Forward/Ndf: selectGroupedByExpiry enables grouping without changing the saved user preference.
 */
export function StrategyPanel(props: StrategyPanelProps) {
  const { tileId } = props;
  const {
    currentUser: { userType },
  } = useUserContext();

  const option = useOptionSelector((option) => option);
  const { legs, hedgeType, uiState } = option.properties;
  const mode: StrategyOrientation = uiState.strategyPanelMode === 'horizontal' ? 'horizontal' : 'vertical';
  const productName = option.type;
  const quote = getQuote(option);
  const groupedByExpiry = selectGroupedByExpiry(option);
  const fields = getStrategyFields(option, userType === 'Internal Sales');
  const isTriggerOrBinary = hasTriggerOrBinary(option);

  const queuedDispatch = useQueuedDispatch();
  const [collapsedExpiries, setCollapsedExpiries] = useState<ReadonlySet<string>>(() => new Set());
  const strategySections  = getStrategySections({ legs, hedgeType, groupedByExpiry, collapsedExpiries });

  function toggleGroup(expiryDate: string) {
    if (strategySections .length === 1) return;

    setCollapsedExpiries((previous) => {
      const expiries = [...previous];
      return new Set(
        previous.has(expiryDate) ? expiries.filter((expiry) => expiry !== expiryDate) : [...expiries, expiryDate],
      );
    });
  }

  async function addLeg() {
    await queuedDispatch(addOptionLegThunk({ tileId }));
  }

  const fieldCount =
    mode === 'horizontal' ? fields.flat().length : fields.reduce((total, field) => total + getFieldSpan(field), 0);
  const nbFields = fieldCount + 1 + (groupedByExpiry && mode === 'vertical' ? 1 : 0);

  return (
    <div className="d-flex flex-column flex-grow-1 border-start bg-lvl2">
      <StrategyPanelHeader />
      <div className="strategy-container" style={{ '--nb-fields': nbFields } as CSSProperties}>
        <div
          className={clsx('strategy-grid', mode, {
            grouped: groupedByExpiry,
            'with-hedges': fields.flat().some(isHedgeField),
          })}
        >
          {groupedByExpiry && mode === 'vertical' && <div className="grid-cell header">Groups</div>}

          <AddLegButton onClick={addLeg} visible={productName === 'Vanilla' || isTriggerOrBinary} />

          <StrategyFieldHeaders tileId={tileId} fields={fields} mode={mode} />

          {strategySections .map((group) => (
            <StrategySection
              key={`group-${group.expiryDate}`}
              tileId={tileId}
              group={group}
              fields={fields}
              mode={mode}
              groupedByExpiry={groupedByExpiry}
              productName={productName}
              quote={quote}
              isTriggerOrBinary={isTriggerOrBinary}
              onToggle={() => toggleGroup(group.expiryDate)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function AddLegButton(props: { onClick: () => void; visible: boolean }) {
  return (
    <div className="grid-cell header">
      {props.visible && (
        <Button iconPosition="end" purpose="flat" color="secondary" onClick={props.onClick} data-e2e="addLeg-button">
          <b>
            <FormattedMessage id="option.actions.addLeg" />
          </b>
          <Icon icon="add" />
        </Button>
      )}
    </div>
  );
}
